import { Avatar } from 'components/Avatar/Avatar';
import { Button } from 'components/Button/Button';
import { Textarea } from 'components/Textarea/Textarea';
import dayjs from 'dayjs';
import React, { useState } from 'react';

interface ProfileChangeCardProps {
    changeRequest: any;
    onApprove: (comment: string) => void;
    onReject: (comment: string) => void;
}

export const ProfileChangeCard: React.FC<ProfileChangeCardProps> = ({
    changeRequest,
    onApprove,
    onReject,
}) => {
    const [showRejectForm, setShowRejectForm] = useState(false);
    const [rejectComment, setRejectComment] = useState('');
    const [approveComment, setApproveComment] = useState('');

    const member = changeRequest.member;
    const changes = changeRequest.requestedChangesDisplay || {};

    return (
        <div className="bg-white rounded-lg shadow p-6">
            {/* Member Info */}
            <div className="flex items-center gap-4 mb-6">
                <Avatar
                    src={member.image?.url}
                    alt={`${member.firstName} ${member.lastName}`}
                    size="large"
                />
                <div>
                    <h3 className="text-lg font-semibold">
                        {member.firstName} {member.lastName}
                    </h3>
                    <p className="text-gray-600">{member.email}</p>
                    <p className="text-sm text-gray-500">
                        Submitted: {dayjs(changeRequest.createdAt).format('MMMM D, YYYY')}
                    </p>
                </div>
            </div>

            {/* Changes */}
            <div className="space-y-3 mb-6">
                <h4 className="font-semibold text-gray-700">Requested Changes:</h4>
                {Object.entries(changes).map(([field, change]: [string, any]) => (
                    <div key={field} className="bg-gray-50 p-4 rounded">
                        <div className="font-medium text-sm text-gray-700 mb-2">
                            {change.label}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <div className="text-xs text-gray-500 mb-1">Current:</div>
                                <div className="text-sm">
                                    {change.old || <span className="text-gray-400">(empty)</span>}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500 mb-1">Requested:</div>
                                <div className="text-sm font-medium text-blue-600">
                                    {change.new}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Actions */}
            {!showRejectForm ? (
                <div className="flex gap-3">
                    <div className="flex-1">
                        <Textarea
                            placeholder="Optional approval comment..."
                            value={approveComment}
                            onChange={(e) => setApproveComment(e.target.value)}
                            rows={2}
                        />
                    </div>
                    <div className="flex gap-2">
                        <Button
                            onClick={() => onApprove(approveComment)}
                            variant="default"
                        >
                            Approve
                        </Button>
                        <Button
                            onClick={() => setShowRejectForm(true)}
                            variant="destructive"
                        >
                            Reject
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="space-y-3">
                    <Textarea
                        placeholder="Rejection reason (required)..."
                        value={rejectComment}
                        onChange={(e) => setRejectComment(e.target.value)}
                        rows={3}
                        required
                    />
                    <div className="flex gap-2">
                        <Button
                            onClick={() => {
                                if (rejectComment.trim()) {
                                    onReject(rejectComment);
                                }
                            }}
                            variant="destructive"
                            disabled={!rejectComment.trim()}
                        >
                            Confirm Rejection
                        </Button>
                        <Button
                            onClick={() => {
                                setShowRejectForm(false);
                                setRejectComment('');
                            }}
                            variant="outline"
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};
